import { apiClient } from './client'
import type { CursorPage } from './accounts'
import type { IngestionPipelineView, IngestionRunView, SourceSystemView } from '../types/ingest'

export interface ListPageParams {
  cursor?: string | null
  limit?: number
}

function toQuery(params: ListPageParams): string {
  const q = new URLSearchParams()
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  return q.toString()
}

export interface ListIngestionRunsParams extends ListPageParams {
  status?: string | null
  pipelineId?: string | null
}

function toRunsQuery(params: ListIngestionRunsParams): string {
  const q = new URLSearchParams(toQuery(params))
  if (params.status) q.set('status', params.status)
  if (params.pipelineId) q.set('pipeline_id', params.pipelineId)
  return q.toString()
}

/**
 * `GET /ingestion-runs` (IB-282 View 4, AC4.2 / I-48, NEW route) — the ops dashboard's "Ingestion
 * runs/day" tile walks this bounded, keyset-paged, `ingestion_run:R`-gated list client-side (design
 * §2, ≤10 pages/2,000 rows per window — `useOperationsAnalytics`'s own bound, not enforced here).
 */
export async function listIngestionRuns(
  params: ListIngestionRunsParams = {},
  signal?: AbortSignal,
): Promise<CursorPage<IngestionRunView>> {
  const { data } = await apiClient.get<CursorPage<IngestionRunView>>(
    `/ingestion-runs?${toRunsQuery(params)}`,
    { signal },
  )
  return data
}

/** `GET /source-systems` (already-published `SourceSystemController`) — the Data screen's "Import sources". */
export async function listSourceSystems(
  params: ListPageParams = {},
  signal?: AbortSignal,
): Promise<CursorPage<SourceSystemView>> {
  const { data } = await apiClient.get<CursorPage<SourceSystemView>>(`/source-systems?${toQuery(params)}`, { signal })
  return data
}

/** `GET /ingestion-pipelines` (already-published `IngestionPipelineController`) — "Ingestion pipelines". */
export async function listIngestionPipelines(
  params: ListPageParams = {},
  signal?: AbortSignal,
): Promise<CursorPage<IngestionPipelineView>> {
  const { data } = await apiClient.get<CursorPage<IngestionPipelineView>>(
    `/ingestion-pipelines?${toQuery(params)}`,
    { signal },
  )
  return data
}
